import { useSearchEventsMutation } from "@app/data/events/eventsApi"
import { IoError, ioErrorOf } from "@app/data/errors"
import type { EventInfo } from "../events-feed/types"
import { Location } from "@components/feed-map/types"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import _ from "lodash"
import useEventFilter from "@shared/filter/hooks/useEventFilter"
import { generateLocationFilter } from "@shared/map-events/locationFilter"
import { SearchEventDto } from "@app/data/tribeSwaggerApi"
import { useAppSelector } from "@app/hooks"
import { selectCurrentLocation } from "@shared/location/selectors"
import { mapCache, isFresh } from "./mapCache"

export type MapSearchEvents = {
  isLoading: boolean
  error?: IoError
  // Сообщить хуку, что пользователь сдвинул/приблизил карту
  onViewChange: (location: Location) => void
  data?: EventInfo[]
  isAllRussia: boolean
}

const PAGE_SIZE = 500
const ALL_RUSSIA_PAGES = [0, 1, 2] // до 1500 событий — с кластеризацией это не тормозит

/**
 * Загрузка событий для карты.
 * - «Вся Россия»: грузим один раз на каждый набор фильтров, при сдвиге карты НЕ перезапрашиваем.
 * - Конкретный город: грузим события в видимой области, перезапрос через 600 мс после остановки карты.
 * - Ответ на устаревший запрос игнорируется (если пользователь успел сдвинуть карту или сменить фильтр).
 */
export const useMapSearchEvents = (): MapSearchEvents => {
  const { currentFilter: initialFilter } = useEventFilter()
  const initialLocation = useAppSelector(selectCurrentLocation)
  const currentFilter = initialFilter
  const currentLocation = initialLocation
  const [trigger] = useSearchEventsMutation()

  const isAllRussia =
    !currentLocation ||
    currentLocation.city === "Вся Россия" ||
    (currentLocation.latitude === 0 && currentLocation.longitude === 0)

  // «Все» выключена и категория не выбрана — на карте тоже пусто (как в ленте)
  const feedOff = currentFilter.showAll !== true &&
    !(Array.isArray(currentFilter.eventTypeId) && currentFilter.eventTypeId.length > 0)
  const filterKey = JSON.stringify({ ...currentFilter, showAll: undefined, feedOff })
  const cityKey = isAllRussia ? "ALL" : `${currentLocation?.latitude}:${currentLocation?.longitude}`
  const dataKey = `${cityKey}|${filterKey}`
  const restoredFromCache = useRef(mapCache.dataKey === dataKey && !!mapCache.events && isFresh())

  const [events, setEvents] = useState<SearchEventDto[]>(() => restoredFromCache.current ? mapCache.events! : [])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<any>(undefined)

  const requestId = useRef(0)
  const lastView = useRef<Location | null>(restoredFromCache.current && mapCache.view ? (mapCache.view as unknown as Location) : null)

  // Всегда актуальные значения для отложенных вызовов (без устаревших замыканий)
  const latest = useRef({ currentFilter, isAllRussia, dataKey, feedOff })
  latest.current = { currentFilter, isAllRussia, dataKey, feedOff }

  const load = useCallback(async (view: Location | null) => {
    const { currentFilter: filter, isAllRussia: allRussia, feedOff: off } = latest.current
    if (off) {
      requestId.current++        // обесцениваем идущие запросы
      setEvents([])
      setIsLoading(false)
      return
    }
    if (!allRussia && !view) return
    const id = ++requestId.current
    setIsLoading(true)
    setError(undefined)
    try {
      let content: SearchEventDto[]
      if (allRussia) {
        const baseFilter = { ...filter, latitude: undefined, longitude: undefined, distanceInMeters: undefined, city: undefined }
        const results = await Promise.all(
          ALL_RUSSIA_PAGES.map(page => trigger({ size: PAGE_SIZE, page, filter: baseFilter }).unwrap())
        )
        content = results.flatMap(r => r.content)
      } else {
        const data = await trigger({ size: PAGE_SIZE, page: 0, filter: generateLocationFilter(view!, filter) }).unwrap()
        content = data.content
      }
      if (id !== requestId.current) return // пришёл ответ на старый запрос
      // Убираем дубли (при параллельных страницах одно событие может прийти дважды)
      const unique = _.uniqBy(content, "eventId")
      setEvents(unique)
      // запоминаем — при возврате на карту покажем сразу, без загрузки
      mapCache.events = unique
      mapCache.dataKey = latest.current.dataKey
      mapCache.savedAt = Date.now()
    } catch (e) {
      if (id === requestId.current) setError(e)
    } finally {
      if (id === requestId.current) setIsLoading(false)
    }
  }, [trigger])

  // Один debounce на всё время жизни компонента (раньше создавался новый на каждый вызов и не работал)
  const debouncedLoad = useMemo(() => _.debounce((view: Location) => load(view), 600), [load])
  useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad])

  const onViewChange = useCallback((view: Location) => {
    const prev = lastView.current as unknown as { zoom?: number; center?: number[] } | null
    const same = !!prev && prev.zoom === (view as any).zoom &&
      Math.abs((prev.center?.[0] ?? 0) - ((view as any).center?.[0] ?? 0)) < 1e-6 &&
      Math.abs((prev.center?.[1] ?? 0) - ((view as any).center?.[1] ?? 0)) < 1e-6
    lastView.current = view
    if (latest.current.isAllRussia) return // для всей России область не важна
    if (same) return // вид не изменился (например, карта только что открылась на прежнем месте)
    debouncedLoad(view)
  }, [debouncedLoad])

  // Перезапрос при смене фильтров или города — сразу, без ожидания движения карты
  const lastCityKey = useRef(cityKey)
  useEffect(() => {
    debouncedLoad.cancel()
    // Вернулись на карту с теми же фильтрами и городом — события уже есть, не грузим заново
    if (restoredFromCache.current) {
      restoredFromCache.current = false
      lastCityKey.current = cityKey
      return
    }
    if (isAllRussia) {
      load(null)
    } else if (currentLocation) {
      // Пока карта не сообщила свою область — берём примерно 30×30 км вокруг центра города
      const lat = currentLocation.latitude
      const lng = currentLocation.longitude
      const view: Location = lastView.current && cityKey === lastCityKey.current
        ? lastView.current
        : { center: [lng, lat], zoom: 12, bounds: [[lng - 0.25, lat + 0.15], [lng + 0.25, lat - 0.15]] } as Location
      load(view)
    }
    lastCityKey.current = cityKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, cityKey])

  return {
    isLoading,
    onViewChange,
    error: ioErrorOf(error),
    data: events,
    isAllRussia,
  }
}
